
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { stripTags } from 'app/shared/helpers/html.helper';
import { UsersDropdownComponent } from './users-dropdown/users-dropdown.component';
import { ButtonModule } from 'primeng/button';
import { NgIf } from '@angular/common';

export interface TaggableUser {
  id: number;
  name: string;
  image?: string;
}

enum KEYBOARD {
  ARROW_LEFT = 'ArrowLeft',
  ARROW_UP = 'ArrowUp',
  ARROW_RIGHT = 'ArrowRight',
  ARROW_BOTTOM = 'ArrowDown',
  ENTER = 'Enter',
  TAB = 'Tab',
  ESCAPE = 'Escape',
}


interface Tag {
  start: number;
  end: number;
  text: string;
}

@Component({
    selector: 'app-input-comment',
    templateUrl: './input-comment.component.html',
    styleUrls: ['./input-comment.component.scss'],
    standalone: true,
    imports: [NgIf, UsersDropdownComponent, ButtonModule]
})
export class InputCommentComponent implements AfterViewInit {
  @Output() send = new EventEmitter<string>();
  @ViewChild('input') input: ElementRef;
  @Input() comment: string;
  @Input() placeholder: string;
  // To authorize to tag people
  @Input() taggable = false;
  @Input() taggableList: Array<TaggableUser> = [];
  @ViewChild('usersDropdown') dropdown: UsersDropdownComponent;
  @Input() set value(newValue: string) {
    this._value = newValue;
    this.updateInputFromValue();
  }
  // tslint:disable-next-line: variable-name
  private _value: string;
  isAfterInitHasBeenCalled = false;
  isDropdownOpened = false;
  usernameSearch = '';
  tagsInComment: Array<Tag> = [];
  commentWithoutBBTags: string;

  constructor(private readonly renderer: Renderer2) {

  }

  ngAfterViewInit() {
    this.isAfterInitHasBeenCalled = true;
    if (this._value) {
      this.updateInputFromValue();
    }
  }

  updateInputFromValue() {
    if (!this.isAfterInitHasBeenCalled) {
      return;
    }
    this.comment = this._value;
    const commentHtml = this.commentStringToHtml(this.comment);
    this.renderer.setProperty(this.input.nativeElement, 'innerHTML', commentHtml);
  }

  onClickSend(message: string) {
    if (message === '') {
      return;
    }
    this.setCommentHtmlToString();
    const commentToSend = stripTags(this.comment);
    this.send.emit(commentToSend);
    this.renderer.setProperty(this.input.nativeElement, 'innerHTML', '');
    this.comment = '';
  }

  onInput(event: InputEvent) {
    const htmlComment = (event.target as HTMLDivElement).innerHTML;
    this.setCommentHtmlToString();
    if (this.taggable) {
      this.tagsInComment = this.findTagsInComment();
      this.updateDropdownData();
    }
  }

  private setCommentHtmlToString() {
    let comment = '';
    const children = this.input.nativeElement.childNodes;
    for (const i in children) {
      if (!children.hasOwnProperty(i)) {
          continue;
      }
      const childNode = children[i];
      if (childNode.nodeName === '#text') {
          comment += (childNode as Text).data;
      } else if (childNode.nodeName === 'BR') {
          comment += '\n';
      } else if (childNode.nodeName === 'DIV') {
        const previousChild = children[(i as any as number) - 1];
        if (previousChild && previousChild.nodeName === '#text') {
          comment += '\n';
        }
        comment += (childNode as HTMLDivElement).innerText + '\n';
      } else if (childNode.nodeName === 'SPAN' && childNode.className === 'tag-link') {
        comment += '[[tag]]' + (childNode as HTMLSpanElement).innerText + '[[/tag]]';
      } else {
        // In case where element contains tag in its childs
        childNode.innerHTML = childNode.innerHTML.replace(
          /<span class="tag-link".*>(.*)<\/span>/g,
          (_, tagname) => `[[tag]]${tagname}[[/tag]]`
        );
        comment += childNode.innerText;
      }
    }

    this.commentWithoutBBTags = comment.replace(/\[\[tag\]\]|\[\[\/tag\]\]/g, '');
    this.comment = comment;
  }

  private commentStringToHtml(comment: string) {
    return comment
      .replace(/(\r\n|\n\r|\r|\n)/g, '<br>')
      .replace(/\[\[tag\]\](.*?)\[\[\/tag\]\]/g, (_, tagname) => {
        return `<span class="tag-link" contenteditable="false">${tagname}</span>`;
      });
  }

  onUserChosen(user: TaggableUser) {
    const sel = window.getSelection();
    const selectedNodeIndex = this.getElementIndexInChildNodes(this.input.nativeElement, sel.anchorNode);
    const textBeforeTag = this.computeTotalTextBeforechildElement(this.input.nativeElement, sel.anchorNode);
    const tag = this.getTagUnderCursor(sel, textBeforeTag);
    if (!tag) {
      return;
    }
    // -1 to cut before '@'
    const startTag = tag.start - 1;
    const positionInTag = (textBeforeTag.length + sel.anchorOffset) - tag.start;
    const commentFirstPart = this.comment.slice(0, startTag);
    const commentSecondPart = this.comment.slice(startTag + positionInTag + 1);
    const tagLink = `<span class="tag-link" contenteditable="false">@${user.name}</span>&nbsp;`;
    const commentHtml = commentFirstPart + tagLink + commentSecondPart;
    this.renderer.setProperty(this.input.nativeElement, 'innerHTML', this.commentStringToHtml(commentHtml));
    this.setCommentHtmlToString();

    if (selectedNodeIndex !== -1) {
        let newElementToSelect = this.input.nativeElement.childNodes[selectedNodeIndex + 2];
        if (!newElementToSelect) {
            newElementToSelect = this.input.nativeElement.childNodes[selectedNodeIndex + 1];
        }
        if (newElementToSelect) {
            const range = document.createRange();
            range.setStart(newElementToSelect, 1);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
        }
    }
    this.updateDropdownData();
  }

  onKeydown(event: KeyboardEvent) {
    if (!this.taggable) {
      return;
    }
    const code = event.code;
    if (code === KEYBOARD.ARROW_LEFT) {
      setTimeout(this.updateDropdownData.bind(this));
    } else if (code === KEYBOARD.ARROW_UP) {
      this.moveUserSelection(event, 'up');
    } else if (code === KEYBOARD.ARROW_RIGHT) {
      setTimeout(this.updateDropdownData.bind(this));
    } else if (code === KEYBOARD.ARROW_BOTTOM) {
      this.moveUserSelection(event, 'down');
    } else if (code === KEYBOARD.ENTER || code === KEYBOARD.TAB) {
      const sel = window.getSelection();
      const tag = this.getTagUnderCursor(sel);
      if (tag) {
        event.preventDefault();
        setTimeout(this.dropdown.chooseSelectedUser.bind(this.dropdown));
      }
    } else if (code === KEYBOARD.ESCAPE) {
      if (this.isDropdownOpened) {
        this.isDropdownOpened = false;
        event.preventDefault();
      }
    }
  }

  private getElementIndexInChildNodes(parentNode: Node, element: Node): number {
    const children = parentNode.childNodes;
    for (const i in children) {
        if (!children.hasOwnProperty(i)) {
            continue;
        }
        if (element === children[i]) {
            return +i;
        }
        if (this.isChildOf(element, children[i])) {
            return +i;
        }
    }
    return -1;
  }

  private moveUserSelection(event: KeyboardEvent, direction: 'up' | 'down') {
    const selection = window.getSelection();
    const tag = this.getTagUnderCursor(selection);
    if (tag) {
        event.preventDefault();
        if (direction === 'up') {
          this.dropdown.moveSelectionUp();
        } else {
          this.dropdown.moveSelectionDown();
        }
    }
  }

  updateDropdownData() {
    const sel = window.getSelection();
    const textBeforeTag = this.computeTotalTextBeforechildElement(this.input.nativeElement, sel.anchorNode);
    const selectedTag = this.getTagUnderCursor(sel, textBeforeTag);
    if (selectedTag) {
      const positionInTag = (textBeforeTag.length + sel.anchorOffset) - selectedTag.start;
      const search = selectedTag.text.substring(0, positionInTag);
      this.usernameSearch = search;
      this.isDropdownOpened = true;
    } else {
      this.isDropdownOpened = false;
    }
  }

  findTagsInComment(): Array<Tag> {
    const matches = [...this.comment.matchAll(/(^|\s)@([^\s]*)/g)];
    const tags = matches.map<Tag>((match) => {
      // Start of match[2] : index + space length if exists + '@' length
      const startIndex = match.index + match[1].length + 1;
      return {
        start: startIndex,
        end: startIndex + match[2].length,
        text: match[2]
      };
    });
    return tags;
  }

  // Possible to give textBeforeSelectedElement in parameter to avoid multiple greedy computations
  getTagUnderCursor(selection: Selection, textBeforeSelectedElement?: string): Tag | null {
    textBeforeSelectedElement = textBeforeSelectedElement ??
      this.computeTotalTextBeforechildElement(this.input.nativeElement, selection.anchorNode);
    const cursorPosition = textBeforeSelectedElement.length + selection.anchorOffset;
    const selectedTag = this.tagsInComment.find((tag) => cursorPosition >= tag.start && cursorPosition <= tag.end);
    return selectedTag ?? null;
  }

  private computeTotalTextBeforechildElement(parentElement: Node, element: Node) {
    const children = parentElement.childNodes;
    let totalTextBefore = '';
    for (const i in children) {
        if (!children.hasOwnProperty(i)) {
            continue;
        }
        const childNode = children[i];
        if (childNode === element) {
          if (element.nodeName === 'div') {
            totalTextBefore += '\n';
          }
          return totalTextBefore;
        }
        if (childNode.nodeName === '#text') {
          totalTextBefore += (childNode as Text).data;
        } else if (childNode.nodeName === 'BR') {
          totalTextBefore += '\n';
        } else if (childNode.nodeName === 'DIV') {
          const previousChild = children[(i as any as number) - 1];
          if (previousChild && previousChild.nodeName === '#text') {
            totalTextBefore += '\n';
          }
          // Element could be contained by on child of parent element
          if (this.isChildOf(element, childNode)) {
            return totalTextBefore;
          }
          totalTextBefore += (childNode as HTMLDivElement).innerText + '\n';
        } else if (childNode.nodeName === 'SPAN' || (childNode as HTMLSpanElement).className === 'tag-link') {
          // Element could be contained by on child of parent element
          if (this.isChildOf(element, childNode)) {
            return totalTextBefore;
          }
          // Add tag in bb code to be consistent to comment
          totalTextBefore += `[[tag]]${(childNode as HTMLSpanElement).innerText}[[/tag]]`;
        } else {
          // Element could be contained by on child of parent element
          if (this.isChildOf(element, childNode)) {
            return totalTextBefore;
          }
          // Add tag in bb code to be consistent to comment
          (childNode as HTMLElement).innerHTML = (childNode as HTMLElement).innerHTML.replace(
            /<span class="tag-link".*>(.*)<\/span>/g,
            (_, tagname) => `[[tag]]${tagname}[[/tag]]`
          );

          totalTextBefore += (childNode as HTMLSpanElement).innerText;
        }
    }

    return totalTextBefore;
  }

  private isChildOf(presumedChild: Node, parent: Node) {
    const children = parent.childNodes;
    for (const i in children) {
      if (children[i] && children[i] === presumedChild) {
        return true;
      }
    }
    return false;
  }

  focus() {
    this.input.nativeElement.focus();
  }

  moveCursorAtEnd() {
    const children = this.input.nativeElement.childNodes;
    if (!children.length) {
      return;
    }
    const lastChild = children[children.length - 1];
    let lastChildText;
    if (lastChild.nodeName === '#text') {
      lastChildText = lastChild.data;
    } else {
      lastChildText = lastChild.innerText ?? '';
    }
    const selection = window.getSelection();
    const range = document.createRange();
    range.setStart(lastChild, lastChildText.length);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}
